if ( typeof ShippingNotice !== 'function' ) {

  class ShippingNotice extends HTMLElement {

    constructor() {
      super();
    }

    connectedCallback() {
      // Render with whatever data-cart-total Liquid set on the server.
      this.init();

      // Refresh in real-time whenever the cart changes. We bind a few possible
      // event names because the theme uses several conventions:
      //   - `cart-updated` is dispatched on the <cart-form> element (no bubble)
      //   - `krown:cart:update` / `krown:cart:add` are KROWN-dispatcher events
      //   - `cart:updated` is what our own JTL refresh fires after section swap.
      // Re-fetch /cart.js so we always read the canonical cart.total_price
      // (post-discounts), not the rendered subtotal.
      if (!this._listenersBound) {
        this._listenersBound = true;
        this._onCartUpdate = () => this.refreshFromCartJson();

        document.addEventListener('cart-updated', this._onCartUpdate);
        document.addEventListener('cart:updated', this._onCartUpdate);
        document.addEventListener('krown:cart:update', this._onCartUpdate);
        document.addEventListener('krown:cart:add', this._onCartUpdate);

        // cart-updated on <cart-form> doesn't bubble — listen directly there.
        document.querySelectorAll('cart-form').forEach((cf) => {
          cf.addEventListener('cart-updated', this._onCartUpdate);
        });
      }
    }

    disconnectedCallback() {
      if (this._onCartUpdate) {
        document.removeEventListener('cart-updated', this._onCartUpdate);
        document.removeEventListener('cart:updated', this._onCartUpdate);
        document.removeEventListener('krown:cart:update', this._onCartUpdate);
        document.removeEventListener('krown:cart:add', this._onCartUpdate);
        document.querySelectorAll('cart-form').forEach((cf) => {
          cf.removeEventListener('cart-updated', this._onCartUpdate);
        });
      }
    }

    refreshFromCartJson() {
      fetch('/cart.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
        .then((res) => res.json())
        .then((cart) => {
          // cart.total_price is the post-discount cart total (in cents) —
          // that's what we count toward free shipping.
          this.setAttribute('data-cart-total', cart.total_price);
          this.init();
        })
        .catch(() => { /* swallow — keep the last rendered state */ });
    }

    init() {

      const textEl = this.querySelector('[data-js-free-shipping-text]');
      if (!textEl) return;

      const freeShippingThreshold = Math.round(Number(this.getAttribute('data-free-shipping')) * (Shopify.currency.rate ? Number(Shopify.currency.rate) : 1));
      const cartTotal = Number(this.getAttribute('data-cart-total'));
      const freeShippingRemaining = cartTotal - freeShippingThreshold;

      let cartSliderWidth = 0;
      if ( freeShippingRemaining < 0 ) {
        const remaining = this._formatMoney(Math.abs(freeShippingRemaining), KROWN.settings.shop_money_format);
        // "Add £X.XX for Free Ship" — amount in big blue/bold, "Free Ship" bold.
        textEl.innerHTML =
          'Add <strong class="nt-free-ship__amount">' + remaining +
          '</strong> for <strong class="nt-free-ship__phrase">Free Delivery</strong>';
        cartSliderWidth = 100 - (Math.abs(freeShippingRemaining) * 100 / freeShippingThreshold);
      } else {
        // Threshold met — "Free Ship Unlocked!" with "Free Ship" bold.
        textEl.innerHTML = '<strong class="nt-free-ship__phrase">Free Delivery</strong> Unlocked!';
        cartSliderWidth = 100;
      }
      const slider = this.querySelector('[data-js-free-shipping-slider]');
      if (slider) slider.style.width = `${cartSliderWidth}%`;
    }

    _formatMoney(cents, format) {

      if (typeof cents === 'string') {
        cents = cents.replace('.', '');
      }

      let value = '';
      const placeholderRegex = /\{\{\s*(\w+)\s*\}\}/;
      const formatString = format || moneyFormat;

      function formatWithDelimiters(number, precision, thousands, decimal) {
        thousands = thousands || ',';
        decimal = decimal || '.';

        if (isNaN(number) || number === null) {
          return 0;
        }

        number = (number / 100.0).toFixed(precision);

        const parts = number.split('.');
        const dollarsAmount = parts[0].replace(
          /(\d)(?=(\d\d\d)+(?!\d))/g,
          '$1' + thousands
        );
        const centsAmount = parts[1] ? decimal + parts[1] : '';

        return dollarsAmount + centsAmount + KROWN.settings.iso_code;
      }

      switch (formatString.match(placeholderRegex)[1]) {
        case 'amount':
          value = formatWithDelimiters(cents, 2);
          break;
        case 'amount_no_decimals':
          value = formatWithDelimiters(cents, 0);
          break;
        case 'amount_with_comma_separator':
          value = formatWithDelimiters(cents, 2, '.', ',');
          break;
        case 'amount_no_decimals_with_comma_separator':
          value = formatWithDelimiters(cents, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          value = formatWithDelimiters(cents, 0, ' ');
          break;
        case 'amount_with_apostrophe_separator':
          value = formatWithDelimiters(cents, 2, "'");
          break;
      }

      return formatString.replace(placeholderRegex, value);
    }

  }

  if ( typeof customElements.get('shipping-notice') == 'undefined' ) {
    customElements.define('shipping-notice', ShippingNotice);
  }

}
